import { useMsal } from "@azure/msal-react";
import { hasRole, useAccountId } from "../Auth/auth-functions";
import { AzureADRoles } from "../Auth/auth-roles";
import { WorkOrderDto, WorkOrderStatus } from "../models/workorder.models";
import { ListTeamDto } from "../models/team.models";

export const getWorkOrderStatusDescription = (status: WorkOrderStatus): string => {
	switch (status) {
		case WorkOrderStatus.Requested:
			return 'Requested';
		case WorkOrderStatus.Open:
			return 'Open';
		case WorkOrderStatus.InProgress:
			return 'In Progress';
		case WorkOrderStatus.OnHold:
			return 'On Hold';
		case WorkOrderStatus.Completed:
			return 'Completed';
		case WorkOrderStatus.Archived:
			return 'Archived';
		default:
			return 'Unknown';
	}
}

export const getWorkOrderStatusColor = (status: WorkOrderStatus): string => {
	switch (status) {
		case WorkOrderStatus.Requested:
			return 'teal';
		case WorkOrderStatus.Open:
			return 'blue';
		case WorkOrderStatus.InProgress:
			return 'violet';
		case WorkOrderStatus.OnHold:
			return 'orange';
		case WorkOrderStatus.Completed:
			return 'green';
		case WorkOrderStatus.Archived:
			return 'gray';
		default:
			return 'gray';
	}
}

export const isCreatedByUser = (workOrder: WorkOrderDto | undefined, userId: string) => {
	return workOrder?.CreatedBy?.AzurePrincipalId === userId;
}

export const isCreatedByTeamMember = (workOrder: WorkOrderDto | undefined, teamMemberIds: string[]) => {
	return teamMemberIds.some(userId => workOrder?.CreatedBy?.UserId === userId);
}

export const isAssignedToUser = (workOrder: WorkOrderDto | undefined, userId: string) => {
	return workOrder?.AssignedTo?.Users?.some(user => user.AzurePrincipalId === userId) ?? false;
}

export const isAssignedToTeam = (workOrder: WorkOrderDto | undefined, teamIds: string[]) => {
	return workOrder?.AssignedTo?.Teams?.some(team => teamIds.includes(team.TeamId)) ?? false;
}

export const useWorkOrderPermissions = (workOrder: WorkOrderDto | undefined, teams?: ListTeamDto[]) => {
	const { instance } = useMsal();
	const activeAccount = instance.getActiveAccount();
	const roles = activeAccount?.idTokenClaims!['roles'] ?? [];
	
	const currentUserId = useAccountId();
	const currentTeamIds = teams?.filter(team => team.Members.some(member => member.AzurePrincipalId === currentUserId)).map(team => team.TeamId) ?? [];

	const canCreate = hasRole(roles, AzureADRoles.AdminOrFullUser, false);

	const canUpdate = hasRole(roles, AzureADRoles.Admin, false) && workOrder?.Status !== WorkOrderStatus.Archived || 
		(hasRole(roles, AzureADRoles.FullUser, false) && 
			(isCreatedByUser(workOrder, currentUserId) || 
			isAssignedToUser(workOrder, currentUserId) || 
			isAssignedToTeam(workOrder, currentTeamIds)) &&
			workOrder?.Status !== WorkOrderStatus.Archived) ||
		(hasRole(roles, AzureADRoles.Requester, false) && 
			(isCreatedByUser(workOrder, currentUserId)) &&
			workOrder?.Status === WorkOrderStatus.Requested);

	const canUpdateStatus = hasRole(roles, AzureADRoles.Admin, false) || 
		(hasRole(roles, AzureADRoles.FullUser, false) && 
			(isCreatedByUser(workOrder, currentUserId) || 
			isAssignedToUser(workOrder, currentUserId) || 
			isAssignedToTeam(workOrder, currentTeamIds)));

	const canDelete = hasRole(roles, AzureADRoles.Admin, false) && workOrder?.Status !== WorkOrderStatus.Archived || 
		(hasRole(roles, AzureADRoles.FullUser, false) && 
			isCreatedByUser(workOrder, currentUserId) && 
			workOrder?.Status !== WorkOrderStatus.Completed &&
			workOrder?.Status !== WorkOrderStatus.Archived) ||
		(hasRole(roles, AzureADRoles.Requester, false) && 
			isCreatedByUser(workOrder, currentUserId) && 
			workOrder?.Status === WorkOrderStatus.Requested);

	const canDuplicate = workOrder?.Status !== WorkOrderStatus.Requested;

	return {
		canCreate,
		canUpdate,
		canUpdateStatus,
		canDelete,
		canDuplicate
	};
}